export enum AgentType {
  APPLICATION_SUPPORT_AGENT = "application_support_agent",
  LEASING_AGENT = "leasing_agent",
  /**
   * The outstanding-balance collection agent (PLT-716). Granted by the
   * `ui_collections_outstanding_balance` flag rather than added through the
   * agent wizard, so the API materializes it on the agents list and refuses
   * both creation and deletion of it.
   */
  COLLECTION_AGENT = "collection_agent"
}

/**
 * The types a partner user can add through the agent wizard. Mirrors the API's
 * PARTNER_MANAGED_TYPES: the collection agent arrives with its feature flag, so
 * the wizard's per-type maps are keyed on this rather than on every AgentType.
 */
export type AddableAgentType = Exclude<AgentType, AgentType.COLLECTION_AGENT>;

// Mirrors the API's personality_preset enum. When custom, personality_text
// holds the partner-authored prompt.
export type PersonalityPreset = "friendly" | "professional" | "custom";

export interface LeasingAgent {
  id: number;
  agent_id: number;
  phone_number: string;
  timezone: string;
  all_properties: boolean;
  scoped_properties: { id: number; name: string }[];
  active: boolean;
  wizard_step: number;
  wizard_status: string;
  provisioned_phone_number_id: number | null;
  greeting_message_text: string | null;
  label: string;
  created_at: string;
}

// A trimmed config summary for the agents list (phone chips). Distinct from
// the wizard's fuller LeasingAgentConfig in settings-users-add-agent.
export interface AgentConfigSummary {
  id: number;
  kind: "default" | "phone" | "mailbox";
  provisioned_phone_number?: {
    id: number;
    phone_number: string;
    name: string | null;
  } | null;
}

// Mirrors the API's step_type check constraint.
export enum CollectionSequenceStepType {
  BalanceDueReminder = "balance_due_reminder",
  BalanceDueNotice = "balance_due_notice",
  FinalNoticeBeforeHandoff = "final_notice_before_handoff",
  HandOffToAssignedUser = "hand_off_to_assigned_user"
}

export interface CollectionSequenceStep {
  id: number;
  day_number: number;
  step_type: CollectionSequenceStepType;
  position: number;
  email_enabled: boolean;
  sms_enabled: boolean;
  voice_enabled: boolean;
}

// The number the collection agent calls from. `callable` is not the same as
// "picked": the server imports the number into the voice provider's workspace
// off the request path, and the agent cannot call from it until that lands.
export interface CollectionAgentVoiceNumber {
  id: number;
  phone_number: string;
  name: string | null;
  elevenlabs_registration_status: string | null;
  callable: boolean;
}

export interface CollectionAgentConfig {
  id: number;
  boom_ai_agent_id: number;
  minimum_outstanding_balance_cents: number;
  maximum_calls_per_week: number;
  provisioned_phone_number_id: number | null;
  provisioned_phone_number?: CollectionAgentVoiceNumber;
  sequence_steps: CollectionSequenceStep[];
  created_at: string;
  updated_at: string;
}

export interface Agent {
  id: number;
  type: AgentType;
  nickname: string;
  active: boolean;
  wizard_step: number | null;
  wizard_status: string | null;
  personality_preset: PersonalityPreset | null;
  personality_text: string | null;
  created_at: string;
  leasing_agent: LeasingAgent | null;
  configs?: AgentConfigSummary[];
  // Present only on collection agents.
  collection_config?: CollectionAgentConfig;
}
